import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Calendar, Clock, ArrowRight, Search, Tag } from 'lucide-react';
import Navbar from '@/components/Navbar';

const blogPosts = [
  {
    id: 'understanding-fractional-ownership',
    title: 'Understanding Fractional Ownership: A Beginner\'s Guide',
    excerpt: 'Learn the fundamentals of fractional real estate investing and how it\'s democratizing access to premium properties for everyday investors.',
    image: 'https://images.unsplash.com/photo-1560518883-ce09059eeffa?w=800&q=80',
    category: 'Education',
    author: 'ALTx Team',
    date: '2024-12-20',
    readTime: '5 min read',
    featured: true
  },
  {
    id: 'indian-real-estate-2025',
    title: 'Indian Real Estate Market Outlook for 2025',
    excerpt: 'Our analysts break down the key trends, emerging hotspots, and investment opportunities in India\'s evolving real estate landscape.',
    image: 'https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=800&q=80',
    category: 'Market Insights',
    author: 'Research Desk',
    date: '2024-12-18',
    readTime: '8 min read',
    featured: true
  },
  {
    id: 'commercial-vs-residential',
    title: 'Commercial vs Residential: Which Investment is Right for You?',
    excerpt: 'A comprehensive comparison of commercial and residential fractional investments, including risk profiles, returns, and liquidity considerations.',
    image: 'https://images.unsplash.com/photo-1497366216548-37526070297c?w=800&q=80',
    category: 'Investment Tips',
    author: 'Investment Team',
    date: '2024-12-15',
    readTime: '6 min read',
    featured: false
  },
  {
    id: 'art-investment-introduction',
    title: 'Art as an Asset Class: Why Smart Investors are Diversifying',
    excerpt: 'Discover how blue-chip artwork is becoming an increasingly popular alternative investment, offering both aesthetic pleasure and financial returns.',
    image: 'https://images.unsplash.com/photo-1578662996442-48f60103fc96?w=800&q=80',
    category: 'Alternative Assets',
    author: 'ALTx Team',
    date: '2024-12-12',
    readTime: '7 min read',
    featured: false
  },
  {
    id: 'tax-benefits-fractional',
    title: 'Tax Benefits of Fractional Real Estate Investment in India',
    excerpt: 'Understanding the tax implications and benefits of investing in fractional real estate, including capital gains and rental income treatment.',
    image: 'https://images.unsplash.com/photo-1554224155-8d04cb21cd6c?w=800&q=80',
    category: 'Finance',
    author: 'Finance Team',
    date: '2024-12-10',
    readTime: '6 min read',
    featured: false
  },
  {
    id: 'due-diligence-checklist',
    title: 'The Ultimate Due Diligence Checklist for Property Investments',
    excerpt: 'A step-by-step guide to evaluating fractional real estate opportunities, from legal verification to market analysis.',
    image: 'https://images.unsplash.com/photo-1450101499163-c8848c66ca85?w=800&q=80',
    category: 'Education',
    author: 'Legal Team',
    date: '2024-12-08',
    readTime: '10 min read',
    featured: false
  }
];

const categories = ['All', 'Education', 'Market Insights', 'Investment Tips', 'Alternative Assets', 'Finance'];

export default function Blog({ user, setUser }) {
  const navigate = useNavigate();
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [searchQuery, setSearchQuery] = useState('');

  const filteredPosts = blogPosts.filter(post => {
    const matchesCategory = selectedCategory === 'All' || post.category === selectedCategory;
    const matchesSearch = post.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         post.excerpt.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  const featuredPosts = blogPosts.filter(post => post.featured);

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric'
    });
  };

  return (
    <div className="min-h-screen bg-[#030303] text-white">
      <Navbar user={user} setUser={setUser} />
      
      <div className="pt-24 pb-16">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-light mb-4">
              <span className="text-white/40">Insights &</span>{' '}
              <span className="text-white">Knowledge</span>
            </h1>
            <p className="text-white/50 max-w-2xl mx-auto text-lg">
              Expert analysis, market trends, and educational content to help you make informed investment decisions.
            </p>
          </motion.div>

          {/* Search and Filter */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="flex flex-col md:flex-row gap-4 mb-12"
          >
            {/* Search */}
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-white/40" />
              <input
                type="text"
                placeholder="Search articles..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-12 pr-4 py-3 bg-[#0A0A0A] border border-white/10 rounded-lg text-white placeholder-white/40 focus:outline-none focus:border-[#4ADE80]/50"
              />
            </div>

            {/* Categories */}
            <div className="flex flex-wrap gap-2">
              {categories.map((category) => (
                <button
                  key={category}
                  onClick={() => setSelectedCategory(category)}
                  className={`px-4 py-2 rounded-lg text-sm transition-all ${
                    selectedCategory === category
                      ? 'bg-[#4ADE80] text-black'
                      : 'bg-[#0A0A0A] text-white/60 hover:text-white border border-white/10'
                  }`}
                >
                  {category}
                </button>
              ))}
            </div>
          </motion.div>

          {/* Featured Posts */}
          {selectedCategory === 'All' && searchQuery === '' && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.3 }}
              className="mb-16"
            >
              <h2 className="text-xl font-light mb-6 flex items-center gap-2">
                <span className="text-[#4ADE80]">★</span> Featured Articles
              </h2>
              <div className="grid md:grid-cols-2 gap-6">
                {featuredPosts.map((post, index) => (
                  <motion.article
                    key={post.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: 0.4 + index * 0.1 }}
                    onClick={() => navigate(`/blog/${post.id}`)}
                    className="group bg-[#0A0A0A] border border-white/5 rounded-xl overflow-hidden hover:border-[#4ADE80]/30 transition-all cursor-pointer"
                  >
                    <div className="relative h-48 overflow-hidden">
                      <img
                        src={post.image}
                        alt={post.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent" />
                      <div className="absolute top-4 left-4">
                        <span className="px-3 py-1 bg-[#4ADE80]/20 text-[#4ADE80] text-xs rounded-full border border-[#4ADE80]/30">
                          {post.category}
                        </span>
                      </div>
                    </div>
                    <div className="p-6">
                      <h3 className="text-lg font-medium mb-2 group-hover:text-[#4ADE80] transition-colors">
                        {post.title}
                      </h3>
                      <p className="text-white/50 text-sm mb-4 line-clamp-2">
                        {post.excerpt}
                      </p>
                      <div className="flex items-center justify-between text-xs text-white/40">
                        <div className="flex items-center gap-4">
                          <span className="flex items-center gap-1">
                            <Calendar className="w-3 h-3" />
                            {formatDate(post.date)}
                          </span>
                          <span className="flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {post.readTime}
                          </span>
                        </div>
                        <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                      </div>
                    </div>
                  </motion.article>
                ))}
              </div>
            </motion.div>
          )}

          {/* All Posts Grid */}
          <div>
            <h2 className="text-xl font-light mb-6">
              {selectedCategory === 'All' ? 'All Articles' : selectedCategory}
              <span className="text-white/40 ml-2">({filteredPosts.length})</span>
            </h2>
            
            {filteredPosts.length === 0 ? (
              <div className="text-center py-16">
                <p className="text-white/40">No articles found matching your criteria.</p>
              </div>
            ) : (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredPosts.map((post, index) => (
                  <motion.article
                    key={post.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: index * 0.05 }}
                    onClick={() => navigate(`/blog/${post.id}`)}
                    className="group bg-[#0A0A0A] border border-white/5 rounded-xl overflow-hidden hover:border-white/20 transition-all cursor-pointer"
                  >
                    <div className="relative h-40 overflow-hidden">
                      <img
                        src={post.image}
                        alt={post.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                    </div>
                    <div className="p-5">
                      <div className="flex items-center gap-2 mb-3">
                        <Tag className="w-3 h-3 text-[#4ADE80]" />
                        <span className="text-xs text-[#4ADE80]">{post.category}</span>
                      </div>
                      <h3 className="text-base font-medium mb-2 group-hover:text-[#4ADE80] transition-colors line-clamp-2">
                        {post.title}
                      </h3>
                      <p className="text-white/50 text-sm mb-4 line-clamp-2">
                        {post.excerpt}
                      </p>
                      <div className="flex items-center justify-between text-xs text-white/40">
                        <span>{formatDate(post.date)}</span>
                        <span>{post.readTime}</span>
                      </div>
                    </div>
                  </motion.article>
                ))}
              </div>
            )}
          </div>

          {/* Newsletter CTA */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="mt-20 bg-gradient-to-r from-[#4ADE80]/10 to-transparent border border-[#4ADE80]/20 rounded-2xl p-8 md:p-12 text-center"
          >
            <h3 className="text-2xl font-light mb-3">Stay Updated</h3>
            <p className="text-white/50 mb-6 max-w-md mx-auto">
              Subscribe to our newsletter for the latest market insights and investment opportunities.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto">
              <input
                type="email"
                placeholder="Enter your email"
                className="flex-1 px-4 py-3 bg-[#0A0A0A] border border-white/10 rounded-lg text-white placeholder-white/40 focus:outline-none focus:border-[#4ADE80]/50"
              />
              <button className="px-6 py-3 bg-[#4ADE80] text-black font-medium rounded-lg hover:bg-[#4ADE80]/90 transition-colors">
                Subscribe
              </button>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
}
